import React from "react";
import { View, Text, StyleSheet, TouchableOpacity, Platform } from "react-native";
import { COLORS } from "../../constants";

/**
 * Chip Component
 *
 * @param {Object} props - The properties passed to the Chip component.
 * @param {Object} props.FontAwesomeIcon - The FontAwesomeIcon component to render the icon.
 * @param {Object} props.icon - The specific icon to be displayed inside the chip.
 * @param {string} props.title - The text label for the chip.
 * @param {number} props.size - The size of the FontAwesomeIcon.
 * @param {string} [props.backgroundColor] - The background color for the chip (fallback: COLORS.backgroundGreen).
 * @param {string} [props.borderColor] - The border color for the chip (fallback: COLORS.primary).
 * @param {string} [props.iconColor] - The color of the icon inside the chip (fallback: COLORS.primary).
 * @param {function} [props.onPress] - The function to call when the chip is pressed.
 * @param {function} [props.type] - The type prop to specify the type of the chip info/button. the defaylt type is info which just displays text.
 *
 * @returns {JSX.Element} The rendered Chip component.
 */
const Chip = ({
  FontAwesomeIcon,
  icon,
  title,
  size,
  backgroundColor,
  borderColor,
  iconColor,
  textColor,
  onPress,
  type = "info",
  accessibilityLabel,
}) => {
  const ChipContent = (
    <View
      accessible={true}
      accessibilityLabel={accessibilityLabel}
      style={[
        styles.chipContainer,
        {
          backgroundColor: backgroundColor || COLORS.backgroundGreen,
          borderColor: borderColor || COLORS.primary,
        },
      ]}
    >
      <FontAwesomeIcon icon={icon} size={size} color={iconColor || COLORS.primary} />
      <Text
        style={[
          styles.chipText,
          {
            color: textColor || COLORS.textDark,
          },
        ]}
      >
        {title}
      </Text>
    </View>
  );

  return type === "button" ? <TouchableOpacity onPress={onPress}>{ChipContent}</TouchableOpacity> : ChipContent;
};

const styles = StyleSheet.create({
  chipContainer: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: COLORS.backgroundGreen,
    borderRadius: 20,
    borderColor: COLORS.primary,
    borderWidth: Platform.OS == "web" ? 2 : 1.5,
    width: "auto",
    alignSelf: "flex-start",
  },
  chipText: {
    fontSize: 14,
    fontWeight: "500",
    marginLeft: 8,
  },
});

export default Chip;
